/**
 * Iniziative commerciali nel checkout (devops #210, #211, #230): anteprima del prezzo e proposta di upgrade.
 *
 * La decisione è del server (`/api/commercial-initiatives/price-preview`, `/api/upgrade-smart/proposal`): qui si
 * costruiscono le richieste e si decide se l'esito ricevuto vale ancora per la scelta corrente. L'ordine ricalcola
 * comunque tutto sul server, quindi un'anteprima sbagliata non può far pagare una cifra diversa da quella decisa.
 */
import { variationNamesMatch } from './variationNames.js';

export const INITIATIVE_LABELS = {
  RINNOVO_SMART: 'Rinnovo Smart',
  UPGRADE_SMART: 'Upgrade Smart',
  RENDE_EXTRA: 'Rende Extra',
  TARGET_TRAINING_PACK: 'Target Training Pack',
  ELITE_CHECK_PROGRESS: 'Elite Check Progress',
};

/** Listino della variazione nella sede scelta: override di sede se c'è (confronto tollerante sul nome), altrimenti prezzo base. */
export function listPriceFor(variation, centerOverrides) {
  if (!variation) return null;
  if (centerOverrides && typeof centerOverrides === 'object') {
    for (const [name, price] of Object.entries(centerOverrides)) {
      if (variationNamesMatch(name, variation.variationName) && Number.isFinite(Number(price))) return Number(price);
    }
  }
  const base = Number(variation.price);
  return Number.isFinite(base) ? base : null;
}

/** Query string dell'anteprima; nullo se manca qualcosa di indispensabile (nessuna chiamata inutile). */
export function previewQuery({ productId, variationName, centerId, listPrice, promoCodeApplied }) {
  if (!productId || !variationName || listPrice === null || listPrice === undefined || !Number.isFinite(Number(listPrice))) {
    return null;
  }
  const p = new URLSearchParams();
  p.set('productId', productId);
  p.set('variationName', variationName);
  if (centerId) p.set('centerId', centerId);
  p.set('listPrice', String(listPrice));
  p.set('promoCodeApplied', promoCodeApplied ? 'true' : 'false');
  return p.toString();
}

/**
 * Prezzo da usare al posto del listino, o `null`. Vale solo se l'anteprima è per la STESSA durata e la STESSA sede
 * scelte adesso. Di norma un codice promozionale toglie l'iniziativa (non cumulabilità per ordine: vince il codice);
 * Rinnovo e Upgrade Smart invece vincono su ogni codice (`promoCodesExcluded`, devops#466).
 */
export function previewPriceFor(preview, selectedVariationName, selectedCenterId, promoActive) {
  if (!preview || !preview.applied) return null;
  if (promoActive && !preview.promoCodesExcluded) return null;
  if (!variationNamesMatch(preview.variationName, selectedVariationName)) return null;
  if ((preview.centerId || '') !== (selectedCenterId || '')) return null;
  const price = Number(preview.finalPrice);
  return Number.isFinite(price) ? price : null;
}

/**
 * Rinnovo o Upgrade Smart si applica alla scelta corrente: allora nessun codice sconto vale (devops#466). Il server lo
 * fa comunque rispettare all'ordine; qui serve a togliere un codice già applicato e a bloccare il campo.
 */
export function smartExcludesPromo(preview, selectedVariationName, selectedCenterId) {
  return !!(preview && preview.promoCodesExcluded)
    && previewPriceFor(preview, selectedVariationName, selectedCenterId, false) !== null;
}

/** La proposta di upgrade è da mostrare: dovuta dal server, non ancora chiusa, e con una durata proposta presente nel catalogo. */
export function shouldShowUpgrade(proposal, dismissed, variations) {
  if (!proposal || !proposal.propose || dismissed || !proposal.proposedDuration) return false;
  return Array.isArray(variations) && variations.some((v) => variationNamesMatch(v.variationName, proposal.proposedDuration));
}
